import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/store'
import { authApi } from '@/api/auth'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@/config/constants'

export const useAuth = () => {
  const { login, logout, isAuthenticated, user } = useAuthStore()
  const navigate = useNavigate()

  const loginMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authApi.loginEmail(email, password),
    onSuccess: async (token) => {
      // Сначала сохраняем токен в localStorage, чтобы interceptor мог его использовать
      localStorage.setItem('access_token', token.access_token)
      localStorage.setItem('refresh_token', token.refresh_token)
      
      // Теперь можем безопасно вызвать getMe с токеном в заголовках
      try {
        const user = await authApi.getMe()
        login(token.access_token, token.refresh_token, user)
        navigate(ROUTES.DASHBOARD)
      } catch (error) {
        console.error('Error getting user after login:', error)
        // Если не удалось получить пользователя, очищаем токены
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
      }
    },
  })

  const telegramLoginMutation = useMutation({
    mutationFn: (initData: string) => {
      console.log('useAuth: Calling loginTelegram with initData length:', initData.length)
      return authApi.loginTelegram(initData)
    },
    onSuccess: async (token) => {
      console.log('useAuth: Telegram login successful, token received')
      // Сначала сохраняем токен в localStorage, чтобы interceptor мог его использовать
      localStorage.setItem('access_token', token.access_token)
      localStorage.setItem('refresh_token', token.refresh_token)
      
      try {
        console.log('useAuth: Getting user info...')
        const user = await authApi.getMe()
        console.log('useAuth: User info received:', user)
        login(token.access_token, token.refresh_token, user)
        navigate(ROUTES.DASHBOARD)
      } catch (error: any) {
        console.error('useAuth: Error getting user:', error)
        console.error('useAuth: Error response:', error.response?.data)
        console.error('useAuth: Error status:', error.response?.status)
        // Если не удалось получить пользователя, очищаем токены
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        throw error // Пробрасываем ошибку дальше
      }
    },
    onError: (error: any) => {
      console.error('useAuth: Telegram login mutation error:', error)
      console.error('useAuth: Error response:', error.response?.data)
      console.error('useAuth: Error status:', error.response?.status)
    },
  })

  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      logout()
      navigate(ROUTES.LOGIN)
    },
  })

  const { data: currentUser } = useQuery({
    queryKey: ['me'],
    queryFn: authApi.getMe,
    enabled: isAuthenticated,
  })

  return {
    login: loginMutation.mutate,
    loginMutation,
    telegramLogin: telegramLoginMutation.mutate,
    logout: logoutMutation.mutate,
    isLoading: loginMutation.isPending || telegramLoginMutation.isPending,
    isAuthenticated,
    user: user || currentUser,
  }
}
